import type { AssetResolver } from '../core/services';
import { familyList, isGenericFamily } from './textRender';

/**
 * Font files stored as project assets, loaded with FontFace under a private
 * family name derived from the asset id, so two projects' "Brand Sans" never
 * collide with each other or with an installed font. Faces are shared between
 * runtimes and removed from `document.fonts` when the last user releases them.
 */

interface Entry {
  family: string;
  refs: number;
  ready: Promise<void>;
  face: FontFace | null;
}

const entries = new Map<string, Entry>();

export function fontAssetFamily(assetId: string): string {
  return `ohmyobs-font-${assetId.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
}

export interface FontAssetHandle {
  /** The private family name, valid once `ready` resolves. */
  readonly family: string;
  /** Rejects with a user-presentable message when the asset is missing or not a font. */
  readonly ready: Promise<void>;
  release(): void;
}

async function load(assets: AssetResolver, assetId: string, entry: Entry): Promise<void> {
  const blob = await assets.getBlob(assetId);
  if (!blob) throw new Error('the font file is missing from the project');
  const face = new FontFace(entry.family, await blob.arrayBuffer());
  try {
    await face.load();
  } catch {
    throw new Error('the file is not a font this browser can read (.ttf, .otf, .woff or .woff2)');
  }
  // Released while loading: never register.
  if (entries.get(assetId) !== entry) return;
  entry.face = face;
  document.fonts.add(face);
}

export function acquireFontAsset(assets: AssetResolver, assetId: string): FontAssetHandle {
  let entry = entries.get(assetId);
  if (!entry) {
    const created: Entry = { family: fontAssetFamily(assetId), refs: 0, ready: Promise.resolve(), face: null };
    created.ready = load(assets, assetId, created);
    // A failed load is not cached: the next acquire (after the user fixes the asset) tries again.
    created.ready.catch(() => {
      if (entries.get(assetId) === created && created.refs === 0) entries.delete(assetId);
    });
    entries.set(assetId, created);
    entry = created;
  }
  const held = entry;
  held.refs++;
  let released = false;
  return {
    family: held.family,
    ready: held.ready,
    release() {
      if (released) return;
      released = true;
      held.refs--;
      if (held.refs > 0 || entries.get(assetId) !== held) return;
      entries.delete(assetId);
      if (held.face) document.fonts.delete(held.face);
      held.face = null;
    },
  };
}

/** Number of asset font faces currently registered (cleanup checks). */
export function loadedFontAssetCount(): number {
  let n = 0;
  for (const e of entries.values()) if (e.face) n++;
  return n;
}

let probe: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null = null;

function probeContext(): CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null {
  if (!probe) {
    const canvas = typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(8, 8) : document.createElement('canvas');
    probe = canvas.getContext('2d') as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
  }
  return probe;
}

const SAMPLE = 'mmmmmmmmmmlliiWWQQ@#0123456789';

/**
 * Whether the browser can draw `family` (installed, or a loaded web or asset
 * font). Compares metrics against three generic fallbacks: a missing family
 * measures exactly like each fallback it falls back to.
 */
export function isFamilyAvailable(family: string): boolean {
  if (isGenericFamily(family)) return true;
  const ctx = probeContext();
  if (!ctx) return true;
  const quoted = `"${family.replace(/"/g, '')}"`;
  for (const generic of ['monospace', 'serif', 'sans-serif']) {
    ctx.font = `72px ${generic}`;
    const base = ctx.measureText(SAMPLE).width;
    ctx.font = `72px ${quoted}, ${generic}`;
    if (ctx.measureText(SAMPLE).width !== base) return true;
  }
  return false;
}

/** A warning when the first named family of a CSS family list cannot be drawn, or null. */
export function missingFontWarning(fontFamily: string): string | null {
  const families = familyList(fontFamily);
  const first = families[0];
  if (!first || isGenericFamily(first) || isFamilyAvailable(first)) return null;
  const fallback = families.slice(1).find((f) => isGenericFamily(f) || isFamilyAvailable(f)) ?? 'the browser default';
  return `Font "${first}" is not available on this computer; showing ${fallback === 'the browser default' ? fallback : `"${fallback}"`} instead.`;
}
